import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	type ResourceStoreContext,
	type RouterDataContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import { fetchUserPersonalization } from '@atlassian/jira-personalization-service/src/services/user/index.tsx';
import { PACKAGE_NAME } from '../constants.tsx';
import { ANALYTICS_ID, RESOURCE_KEY, RESOURCE_TYPE } from './constants.tsx';
import type { PersonalizationApiTraitData, PersonalizationApiTraitDataResponse } from './types.tsx';

const getData = async (
	_: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
): Promise<PersonalizationApiTraitData[] | null> => {
	try {
		if (tenantContext.isAnonymous || !tenantContext.atlassianAccountId) {
			return null;
		}
		const accountId = tenantContext.atlassianAccountId;

		const ssrUrl = `/api/v3/personalization/user/${accountId}`;

		// Use the regular path locally https://atlassian.slack.com/archives/C017Y3EPAQ4/p1760402009102299?thread_ts=1760399382.225529&cid=C017Y3EPAQ4
		const shouldUseSsrUrl =
			__SERVER__ &&
			!(
				globalThis.localDevContext?.PILLAR_LOCAL_DEVELOPMENT ??
				process.env.PILLAR_LOCAL_DEVELOPMENT === 'true'
			);

		const response = shouldUseSsrUrl
			? (
					await performGetRequest<PersonalizationApiTraitDataResponse>(ssrUrl, {
						headers: {
							'x-ssr-service-name': 'tap-delivery',
						},
					})
				)?.attributes
			: await fetchUserPersonalization({ userId: accountId });

		if (response) {
			return response;
		}

		return null;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			error,
			meta: {
				id: ANALYTICS_ID,
				packageName: PACKAGE_NAME,
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}
	return null;
};

export const fetchUserTraitsResource: RouteResource<PersonalizationApiTraitData[] | null> =
	createResource<PersonalizationApiTraitData[] | null>({
		type: RESOURCE_TYPE,
		getKey: () => RESOURCE_KEY,
		getData,
		maxAge: Number.MAX_SAFE_INTEGER,
		isCritical: true,
	});
